import { useState } from "react";
import {
  Card,
  KpiCard,
  Panel,
  PanelHead,
  PanelRow,
  Chip,
  Button,
  Input,
  Select,
  Textarea,
  FieldLabel,
  FieldGroup,
  FieldError,
  Table,
  TableHead,
  TableHeaderCell,
  TableBody,
  TableRow,
  TableCell,
  ProgressBar,
  Pipeline,
  Timeline,
  DialogPrimitives,
  SheetPrimitives,
  TooltipPrimitives,
  DropdownMenuPrimitives,
  ToastPrimitives,
} from "../../shared/ui";

/**
 * Phase 2 scratch page — renders every shared UI primitive in every
 * variant so a reviewer can eyeball spacing, typography, and colour
 * fidelity against the existing .kpi / .aud-card / .kb-drawer designs
 * before Phase 3 starts swapping features over.
 *
 * Mounted at /dev/ui via the router (see AppRouter.jsx) and gated on
 * NODE_ENV === 'development' so it never ships in prod builds.
 */
export default function DevUIPage() {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [sheetOpen, setSheetOpen] = useState(false);
  const [toastOpen, setToastOpen] = useState(false);

  return (
    <TooltipPrimitives.TooltipProvider delayDuration={200}>
      <ToastPrimitives.ToastProvider swipeDirection="right">
        <div className="dark min-h-screen bg-rc-bg text-rc-text-primary p-10 flex flex-col gap-10">
          <header className="flex items-baseline justify-between gap-4 flex-wrap">
            <div>
              <div className="text-[11px] font-bold uppercase tracking-[0.2em] text-rc-accent">Phase 2 preview</div>
              <h1 className="text-2xl font-extrabold mt-1">Shared UI primitives</h1>
              <p className="text-sm text-rc-text-secondary mt-1 max-w-[640px]">
                Scratch page to review Tailwind components before swapping feature pages over.
                Not routed in production. Not linked from the sidebar.
              </p>
            </div>
            <Chip tone="info">dev-only</Chip>
          </header>

          <Section title="KpiCard + Card">
            <div className="grid grid-cols-4 gap-3.5">
              <KpiCard label="Revenue YTD" value="2,484K kr" tone="success" trend={{ direction: "up", text: "+12%" }} />
              <KpiCard label="Cost YTD" value="1,210K kr" tone="crit" trend={{ direction: "down", text: "-4%" }} />
              <KpiCard label="Profit" value="1,274K kr" tone="info" sub="Blended margin 51%" />
              <KpiCard label="Bench" value="3" tone="warn" onClick={() => alert("clicked")} />
            </div>
            <div className="grid grid-cols-3 gap-3.5">
              <Card variant="interactive" className="p-5">
                <div className="text-sm font-bold mb-2">Interactive card</div>
                <p className="text-xs text-rc-text-secondary">Hover for accent top bar + lift.</p>
              </Card>
              <Card variant="panel" tone="critical" className="p-5">
                <div className="text-sm font-bold mb-2">Panel · critical left bar</div>
                <p className="text-xs text-rc-text-secondary">For P1 rows and blocked items.</p>
              </Card>
              <Card variant="panel" tone="warn" className="p-5">
                <div className="text-sm font-bold mb-2">Panel · warn left bar</div>
                <p className="text-xs text-rc-text-secondary">For P2 rows and at-risk audits.</p>
              </Card>
            </div>
          </Section>

          <Section title="Chip">
            <div className="flex flex-wrap gap-2">
              {["default", "info", "success", "warn", "crit", "plan", "muted"].map(tone => (
                <Chip key={tone} tone={tone}>{tone}</Chip>
              ))}
            </div>
            <div className="flex flex-wrap gap-2 mt-3">
              <Chip size="sm" tone="info">small</Chip>
              <Chip size="md" tone="info">medium</Chip>
              <Chip size="lg" tone="info">large</Chip>
              <Chip tone="crit" dot="#f04444">with dot</Chip>
            </div>
          </Section>

          <Section title="Button">
            <div className="flex flex-wrap items-center gap-2">
              {["primary", "secondary", "ghost", "destructive", "success", "warn"].map(v => (
                <Button key={v} variant={v}>{v}</Button>
              ))}
            </div>
            <div className="flex flex-wrap items-center gap-2 mt-3">
              {["xs", "sm", "md", "lg"].map(s => (
                <Button key={s} size={s}>{s}</Button>
              ))}
              <Button disabled>disabled</Button>
            </div>
          </Section>

          <Section title="Form inputs">
            <div className="grid grid-cols-3 gap-4 max-w-[720px]">
              <FieldGroup>
                <FieldLabel required>Name</FieldLabel>
                <Input placeholder="Acme Ltd" />
              </FieldGroup>
              <FieldGroup>
                <FieldLabel>Status</FieldLabel>
                <Select defaultValue="active">
                  <option value="active">Active</option>
                  <option value="pending">Pending</option>
                  <option value="closed">Closed</option>
                </Select>
              </FieldGroup>
              <FieldGroup>
                <FieldLabel>Notes</FieldLabel>
                <Textarea rows={3} placeholder="Anything else…" />
              </FieldGroup>
              <FieldGroup>
                <FieldLabel>With error</FieldLabel>
                <Input error placeholder="invalid" />
                <FieldError>Required field</FieldError>
              </FieldGroup>
            </div>
          </Section>

          <Section title="Panel + rows">
            <Panel className="max-w-[640px]">
              <PanelHead title="Top clients" />
              <PanelRow onClick={() => {}}><span>Acme Logistics</span><span className="font-mono">1,240K kr</span></PanelRow>
              <PanelRow onClick={() => {}}><span>Vista Insurance</span><span className="font-mono">1,085K kr</span></PanelRow>
              <PanelRow onClick={() => {}}><span>Ørsted Nordics</span><span className="font-mono">940K kr</span></PanelRow>
            </Panel>
          </Section>

          <Section title="Table">
            <Table>
              <TableHead>
                <tr>
                  <TableHeaderCell>Project</TableHeaderCell>
                  <TableHeaderCell sorted>Due</TableHeaderCell>
                  <TableHeaderCell>Status</TableHeaderCell>
                </tr>
              </TableHead>
              <TableBody>
                <TableRow tone="critical" onClick={() => {}}>
                  <TableCell>Migration discovery</TableCell>
                  <TableCell className="font-mono">2026-04-28</TableCell>
                  <TableCell><Chip size="sm" tone="crit">overdue</Chip></TableCell>
                </TableRow>
                <TableRow tone="warn">
                  <TableCell>Vendor audit</TableCell>
                  <TableCell className="font-mono">2026-05-02</TableCell>
                  <TableCell><Chip size="sm" tone="warn">due soon</Chip></TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>Steering review</TableCell>
                  <TableCell className="font-mono">2026-05-12</TableCell>
                  <TableCell><Chip size="sm" tone="success">on track</Chip></TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </Section>

          <Section title="ProgressBar">
            <div className="grid grid-cols-3 gap-4 max-w-[640px]">
              <ProgressBar pct={30} tone="default" showLabel />
              <ProgressBar pct={65} tone="info" showLabel />
              <ProgressBar pct={90} tone="success" showLabel />
              <ProgressBar pct={50} tone="warn" showLabel />
              <ProgressBar pct={110} tone="crit" showLabel />
              <ProgressBar pct={40} color="#a78bfa" showLabel size="md" />
            </div>
          </Section>

          <Section title="Pipeline">
            <Pipeline
              stages={[
                { key: "discover", label: "Discover", count: 7, fillPct: 100 },
                { key: "scope",    label: "Scope",    count: 4, fillPct: 70 },
                { key: "match",    label: "Match",    count: 2, fillPct: 40 },
                { key: "won",      label: "Won",      count: 3, fillPct: 100 },
              ]}
              activeIndex={1}
              onStageClick={() => {}}
            />
          </Section>

          <Section title="Timeline">
            <Timeline
              className="max-w-[620px]"
              items={[
                { title: "Stage updated",   meta: "2h ago",  tone: "active", body: "Moved from Scope to Match." },
                { title: "Due date moved",  meta: "1d ago",  tone: "warn",   body: "Extended to 2026-05-02." },
                { title: "Assigned Sarah",  meta: "3d ago",  tone: "done" },
                { title: "Scoping created", meta: "7d ago",  tone: "default" },
              ]}
            />
          </Section>

          <Section title="Dialog / Sheet / Tooltip / DropdownMenu / Toast">
            <div className="flex flex-wrap gap-3">
              <Button onClick={() => setDialogOpen(true)}>Open Dialog</Button>
              <Button variant="secondary" onClick={() => setSheetOpen(true)}>Open Sheet</Button>

              <TooltipPrimitives.Tooltip>
                <TooltipPrimitives.TooltipTrigger asChild>
                  <Button variant="ghost">Hover for tooltip</Button>
                </TooltipPrimitives.TooltipTrigger>
                <TooltipPrimitives.TooltipContent>This is a tooltip.</TooltipPrimitives.TooltipContent>
              </TooltipPrimitives.Tooltip>

              <DropdownMenuPrimitives.DropdownMenu>
                <DropdownMenuPrimitives.DropdownMenuTrigger asChild>
                  <Button variant="secondary">Dropdown</Button>
                </DropdownMenuPrimitives.DropdownMenuTrigger>
                <DropdownMenuPrimitives.DropdownMenuContent>
                  <DropdownMenuPrimitives.DropdownMenuLabel>Options</DropdownMenuPrimitives.DropdownMenuLabel>
                  <DropdownMenuPrimitives.DropdownMenuItem onSelect={() => {}}>Edit</DropdownMenuPrimitives.DropdownMenuItem>
                  <DropdownMenuPrimitives.DropdownMenuItem onSelect={() => {}}>Duplicate</DropdownMenuPrimitives.DropdownMenuItem>
                  <DropdownMenuPrimitives.DropdownMenuSeparator />
                  <DropdownMenuPrimitives.DropdownMenuItem onSelect={() => {}}>Archive</DropdownMenuPrimitives.DropdownMenuItem>
                </DropdownMenuPrimitives.DropdownMenuContent>
              </DropdownMenuPrimitives.DropdownMenu>

              <Button variant="warn" onClick={() => setToastOpen(true)}>Fire toast</Button>
            </div>

            <DialogPrimitives.Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
              <DialogPrimitives.DialogContent>
                <DialogPrimitives.DialogHeader>
                  <DialogPrimitives.DialogTitle>Edit client</DialogPrimitives.DialogTitle>
                  <DialogPrimitives.DialogDescription>
                    Update contact info and save to sync downstream.
                  </DialogPrimitives.DialogDescription>
                </DialogPrimitives.DialogHeader>
                <div className="flex flex-col gap-3">
                  <FieldGroup>
                    <FieldLabel>Name</FieldLabel>
                    <Input defaultValue="Acme Ltd" />
                  </FieldGroup>
                  <FieldGroup>
                    <FieldLabel>Primary contact</FieldLabel>
                    <Input defaultValue="lena@acme.com" />
                  </FieldGroup>
                </div>
                <DialogPrimitives.DialogFooter>
                  <Button variant="secondary" onClick={() => setDialogOpen(false)}>Cancel</Button>
                  <Button onClick={() => setDialogOpen(false)}>Save</Button>
                </DialogPrimitives.DialogFooter>
              </DialogPrimitives.DialogContent>
            </DialogPrimitives.Dialog>

            <SheetPrimitives.Sheet open={sheetOpen} onOpenChange={setSheetOpen}>
              <SheetPrimitives.SheetContent side="right">
                <SheetPrimitives.SheetHeader>
                  <SheetPrimitives.SheetTitle>Audit — Q2 revenue recognition</SheetPrimitives.SheetTitle>
                  <SheetPrimitives.SheetDescription>
                    Reference drawer for Phase 3/4 feature migrations.
                  </SheetPrimitives.SheetDescription>
                </SheetPrimitives.SheetHeader>
                <SheetPrimitives.SheetBody>
                  <div className="space-y-4">
                    <Pipeline
                      stages={[
                        { key: "scoping", label: "Scoping", count: 0, fillPct: 100 },
                        { key: "exec",    label: "Execution", count: 2, fillPct: 50 },
                        { key: "review",  label: "Review", count: 1, fillPct: 20 },
                        { key: "wrapup",  label: "Wrap-up", count: 0, fillPct: 0 },
                      ]}
                      activeIndex={1}
                    />
                    <Timeline items={[
                      { title: "Stage advanced", meta: "now", tone: "active", body: "Scoping → Execution." },
                      { title: "Evidence added", meta: "1h ago", tone: "done" },
                      { title: "Kickoff",        meta: "2d ago", tone: "default" },
                    ]}/>
                  </div>
                </SheetPrimitives.SheetBody>
                <SheetPrimitives.SheetFooter>
                  <Button variant="secondary" onClick={() => setSheetOpen(false)}>Close</Button>
                  <Button>Save</Button>
                </SheetPrimitives.SheetFooter>
              </SheetPrimitives.SheetContent>
            </SheetPrimitives.Sheet>

            <ToastPrimitives.Toast open={toastOpen} onOpenChange={setToastOpen} duration={3000} tone="success">
              <ToastPrimitives.ToastTitle>Saved.</ToastPrimitives.ToastTitle>
              <ToastPrimitives.ToastDescription>Audit advanced to Execution.</ToastPrimitives.ToastDescription>
            </ToastPrimitives.Toast>
            <ToastPrimitives.ToastViewport />
          </Section>
        </div>
      </ToastPrimitives.ToastProvider>
    </TooltipPrimitives.TooltipProvider>
  );
}

function Section({ title, children }) {
  return (
    <section className="flex flex-col gap-3">
      <h2 className="text-[13px] font-bold uppercase tracking-[0.18em] text-rc-accent">{title}</h2>
      {children}
    </section>
  );
}
